import { httpClient } from '../../common/data/httpClient';
import { UnknownApiResponseError } from '../../common/domain/unknownApiResponseError';
import type { CategoryFilterOption, FilterOption } from '../domain/models';
import { categoryOptionsReponseSchema, flavourOptionsResponseSchema, mouthfeelOptionsResponseSchema, tasteOptionsResponseSchema } from './dtos';
import wizardApiMappers from './wizardApiMappers';

type WizardApi = {
  getCategoryOptions: () => Promise<CategoryFilterOption[]>,
  getFlavourOptions: (categoryId: string, flavourIds: string[]) => Promise<FilterOption[]>,
  getTasteOptions: (categoryId: string, flavourIds: string[], tasteIds: string[]) => Promise<FilterOption[]>,
  getMouthfeelOptions: (categoryId: string, flavourIds: string[], tasteIds: string[], mouthfeelIds: string[]) => Promise<FilterOption[]>,
};

const getCategoryOptions = async (): Promise<CategoryFilterOption[]> => {
  const response = await httpClient.get('/api/sensory-wheel-filters');

  if (response.status === 200) {
    const dto = categoryOptionsReponseSchema.parse(response.data);
    return wizardApiMappers.mapCategoryFilterOptions(dto.category.options, []);
  }

  throw new UnknownApiResponseError('Failed to fetch category options');
};

const getFlavourOptions = async (categoryId: string, flavourIds: string[]): Promise<FilterOption[]> => {
  const params = new URLSearchParams();
  params.append('category[]', categoryId);
  flavourIds.forEach(id => params.append('sensorywheel_flavor[]', id));
  const url = `/api/sensory-wheel-filters?${params.toString()}`;

  const response = await httpClient.get(url);
  if (response.status === 200) {
    const dto = flavourOptionsResponseSchema.parse(response.data);
    return wizardApiMappers.mapFilterOptions(dto.sensorywheel_flavor.options, flavourIds);
  }

  throw new UnknownApiResponseError('Failed to fetch flavour options');
};

const getTasteOptions = async (categoryId: string, flavourIds: string[], tasteIds: string[]): Promise<FilterOption[]> => {
  const params = new URLSearchParams();
  params.append('category[]', categoryId);
  flavourIds.forEach(id => params.append('sensorywheel_flavor[]', id));
  tasteIds.forEach(id => params.append('sensorywheel_taste[]', id));
  const url = `/api/sensory-wheel-filters?${params.toString()}`;

  const response = await httpClient.get(url);
  if (response.status === 200) {
    const dto = tasteOptionsResponseSchema.parse(response.data);
    return wizardApiMappers.mapFilterOptions(dto.sensorywheel_taste.options, tasteIds);
  }

  throw new UnknownApiResponseError('Failed to fetch taste options');
};

const getMouthfeelOptions = async (categoryId: string, flavourIds: string[], tasteIds: string[], mouthfeelIds: string[]): Promise<FilterOption[]> => {
  const params = new URLSearchParams();
  params.append('category[]', categoryId);
  flavourIds.forEach(id => params.append('sensorywheel_flavor[]', id));
  tasteIds.forEach(id => params.append('sensorywheel_taste[]', id));
  mouthfeelIds.forEach(id => params.append('sensorywheel_mouthfeel[]', id));
  const url = `/api/sensory-wheel-filters?${params.toString()}`;

  const response = await httpClient.get(url);
  if (response.status === 200) {
    const dto = mouthfeelOptionsResponseSchema.parse(response.data);
    return wizardApiMappers.mapFilterOptions(dto.sensorywheel_mouthfeel.options, mouthfeelIds);
  }

  throw new UnknownApiResponseError('Failed to fetch mouth feel options');
};

const wizardApi: WizardApi = {
  getCategoryOptions,
  getFlavourOptions,
  getTasteOptions,
  getMouthfeelOptions: getMouthfeelOptions,
};

export default wizardApi;
